/**
 * Routing utility for merchant ID detection with subdomain support
 *
 * Production: Uses subdomain routing (rideshare.mydomain.com/locations)
 * Dev/Preview: Uses path-based routing (/rideshare/locations)
 */

/**
 * Determines if we should use subdomain-based routing
 * @returns {boolean} True if subdomain mode should be active
 */
export function isSubdomainMode() {
  // Only use subdomains in production AND not on Vercel preview URLs
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    const hostname = window.location.hostname
    // Don't use subdomain mode for Vercel preview URLs or localhost
    if (hostname.includes('vercel.app') || hostname === 'localhost' || hostname.startsWith('192.168.') || hostname.startsWith('127.0.')) {
      return false
    }
    return true
  }
  return false
}

/**
 * Extracts merchant ID from subdomain or URL path
 * @returns {string} The merchant ID (lowercase)
 */
export function getMerchantId() {
  if (typeof window === 'undefined') {
    return 'rideshare' // SSR fallback
  }

  // Mode 1: Subdomain-based (production only)
  if (isSubdomainMode()) {
    const subdomain = window.location.hostname.split('.')[0]
    // Ignore 'www' subdomain
    if (subdomain && subdomain !== 'www') {
      return subdomain.toLowerCase()
    }
  }

  // Mode 2: Path-based (dev, preview, fallback)
  // Handle HashRouter: extract path from hash (e.g., #/rideshare/locations)
  let pathToCheck = window.location.pathname
  if (window.location.hash && window.location.hash.startsWith('#/')) {
    // Remove # and extract the path
    pathToCheck = window.location.hash.substring(1)
  }

  const pathSegments = pathToCheck.split('/').filter(Boolean)
  const pathMerchant = pathSegments[0]

  // Return path-based merchant ID or default to 'rideshare'
  return pathMerchant?.toLowerCase() || 'rideshare'
}

/**
 * Builds a navigation path that's aware of routing mode
 * @param {string} path - The path to navigate to (e.g., '/locations', '/locations/timing')
 * @param {string} merchantId - The merchant ID (optional, uses current if not provided)
 * @returns {string} The full path to navigate to
 */
export function buildPath(path, merchantId = null) {
  const currentMerchantId = merchantId || getMerchantId()

  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  if (isSubdomainMode()) {
    // Subdomain mode: just return the path (merchant is in subdomain)
    return normalizedPath
  }

  // Path-based mode: prepend merchant ID
  return `/${currentMerchantId}${normalizedPath}`
}

/**
 * Validates if a string is a valid merchant identifier
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid merchant ID format
 */
export function isValidMerchantId(id) {
  if (!id) return false
  // Merchant IDs should be lowercase alphanumeric with optional hyphens
  return /^[a-z0-9-]+$/.test(id)
}

/**
 * Gets the base path for merchant (useful for relative routing)
 * @returns {string} The base path (empty for subdomain mode, /merchantId for path mode)
 */
export function getMerchantBasePath() {
  if (isSubdomainMode()) {
    return ''
  }
  return `/${getMerchantId()}`
}
